import { useMemo } from "react";
import {
  StockMarketEnum,
  StockSortField,
  Token,
  TokenSortField,
} from "../features/api/openData";
import { TokenCardData } from "../components/Chat/OthersReportPanel/types";
import { useStockDashboard, StockResult } from "./useStockDashboard";
import { useTokensRankingInfiniteScroll } from "./useTokensRankingInfiniteScroll";

export interface UseAggregatedReport360Params {
  cardType: string; // 来自 data.card.type
  themes?: string[];
  id?: string;
  market?: string;
}

export interface AggregatedReport360Result {
  data: TokenCardData[];
  isLoading: boolean;
  error: any;
}

/**
 * 聚合 Stock 和 Token 的 REPORT_360 数据
 *
 * 根据 cardType 智能选择数据源：
 * - cardType === "pool" || "token" → 使用 useTokensRankingInfiniteScroll
 * - cardType === "stock" → 使用 useStockDashboard
 * - 其他情况 → 两个 hooks 都 disabled
 *
 * @param params 查询参数
 * @returns 聚合后的360报告数据，统一格式为 TokenCardData[]
 */
export const useAggregatedReport360 = (
  params: UseAggregatedReport360Params,
): AggregatedReport360Result => {
  const { cardType, themes, id, market } = params;

  // 控制启用条件
  const tokenEnabled = cardType === "pool" || cardType === "token";
  const stockEnabled = cardType === "stock";

  // 封装 useTokensRankingInfiniteScroll
  const {
    data: cryptoData,
    isLoading: cryptoLoading,
    error: cryptoError,
  } = useTokensRankingInfiniteScroll({
    queryParams: {
      limit: 6,
      type: "REPORT_360",
      "sort.field": TokenSortField.PRICE_CHANGE_24H,
      themes,
    },
    enabled: tokenEnabled,
  });

  // 封装 useStockDashboard
  const {
    data: stockData,
    isLoading: stockLoading,
    error: stockError,
  } = useStockDashboard({
    type: "REPORT_360",
    sortFiled: StockSortField.PRICE_CHANGE_PERCENTAGE,
    pageSize: 6,
    themes,
    enabled: stockEnabled,
    market: market as StockMarketEnum,
  });

  // 统一数据转换为 TokenCardData[]
  const aggregatedData: TokenCardData[] = useMemo(() => {
    // Token/Pool 类型数据转换
    if (tokenEnabled && cryptoData) {
      return cryptoData.map((token: Token) => ({
        id: token.id,
        name: token.name,
        icon: token.logo,
        symbol: token.symbol,
        className: `token-crypto-${token.symbol?.toLowerCase() || "unknown"}`,
      }));
    }

    // Stock 类型数据转换
    if (stockEnabled && stockData?.data) {
      return stockData.data.map((stock: StockResult) => ({
        id: stock.id,
        name: stock.name,
        icon: stock.logo,
        symbol: stock.symbol,
        className: `token-stock-${stock.symbol?.toLowerCase() || "unknown"}`,
      }));
    }

    return [];
  }, [tokenEnabled, stockEnabled, cryptoData, stockData]);

  return {
    data: aggregatedData?.filter((item) => item.id !== id)?.slice(0, 5),
    isLoading: tokenEnabled ? cryptoLoading : stockLoading,
    error: tokenEnabled ? cryptoError : stockError,
  };
};
